import { Stack, Typography } from "@mui/material"
import { GridColDef } from "@mui/x-data-grid"
import { ReactElement, useMemo } from "react"

import AdminDataGrid from "../../../components/dataDisplay/adminDataGrid/adminDataGrid"
import { IChangeDetail } from "../../../services/middleware/types/dnoThreshold/dnoThresholdTypes"
import { getDisplayName } from "../../../utils/thresholdDictionary/thresholdDictionary"

const CHANGE_COLUMNS: GridColDef[] = [
    {
        field: "field",
        headerName: "DNO Config Field",
        flex: 2,
        valueGetter: (_value: unknown, row: IChangeDetail): string =>
            getDisplayName(row.field),
    },
    {
        field: "oldValue",
        headerName: "Old Value",
        flex: 1,
        valueFormatter: (value: unknown): string => String(value ?? "—"),
    },
    {
        field: "newValue",
        headerName: "New Value",
        flex: 1,
        valueFormatter: (value: unknown): string => String(value ?? "—"),
    },
]

const THRESHOLD_COLUMNS: GridColDef[] = [
    { field: "key", headerName: "Licence Area", flex: 1 },
    {
        field: "field",
        headerName: "Threshold",
        flex: 2,
        valueGetter: (_value: unknown, row: IChangeDetail): string =>
            getDisplayName(row.field),
    },
    {
        field: "oldValue",
        headerName: "Current Value",
        flex: 1,
        valueFormatter: (value: unknown): string => String(value ?? "—"),
    },
    {
        field: "newValue",
        headerName: "New Value",
        flex: 1,
        valueFormatter: (value: unknown): string => String(value ?? "—"),
    },
]
type ChangeSummaryGridsProps = {
    changes: IChangeDetail[]
}

/**
 * Renders change details split into DNO Configuration and
 * Licence Area Thresholds AdminDataGrids. Reused across
 * confirm dialog, review dialog, and change request accordion.
 */
const ChangeSummaryGrids = ({
    changes,
}: ChangeSummaryGridsProps): ReactElement => {
    const configChanges = useMemo(
        () => changes.filter((c) => !c.key),
        [changes]
    )
    const thresholdChanges = useMemo(
        () => changes.filter((c) => !!c.key),
        [changes]
    )

    return (
        <Stack spacing={2}>
            {configChanges.length > 0 && (
                <>
                    <Typography variant="subtitle2">
                        DNO Configuration
                    </Typography>
                    <AdminDataGrid<IChangeDetail>
                        rows={configChanges}
                        columns={CHANGE_COLUMNS}
                        getRowId={(row): string => `config-${row.field}`}
                        itemTypeText="dno-config"
                        isLoading={false}
                        showActions={false}
                        toolbarButtons={["reorder", "filter", "export"]}
                        showTitle={false}
                        hideToolbar
                        initialPageSize={5}
                        initialSortField="field"
                        disableRowSelectionOnClick
                    />
                </>
            )}

            {thresholdChanges.length > 0 && (
                <>
                    <Typography variant="subtitle2">
                        Licence Area Thresholds
                    </Typography>
                    <AdminDataGrid<IChangeDetail>
                        rows={thresholdChanges}
                        columns={THRESHOLD_COLUMNS}
                        getRowId={(row): string => `${row.key}-${row.field}`}
                        itemTypeText="dno-thresholds"
                        isLoading={false}
                        showActions={false}
                        toolbarButtons={["reorder", "filter", "export"]}
                        showTitle={false}
                        hideToolbar
                        initialPageSize={5}
                        initialSortField="key"
                        disableRowSelectionOnClick
                    />
                </>
            )}
        </Stack>
    )
}

export default ChangeSummaryGrids
